import { useState, useEffect } from 'react';
import PageLayout from '@/components/feature/PageLayout';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/base/Breadcrumb';

const milestones = [
  { year: '2018', title: '品牌创立', description: '皇冠App正式成立，以"安全、稳定、便捷"为核心理念，开启移动应用平台的建设之路。' },
  { year: '2019', title: '用户突破百万', description: '凭借出色的产品体验和口碑传播，注册用户数突破100万，月活跃用户快速增长。' },
  { year: '2020', title: '安全认证', description: '通过ISO 27001信息安全认证和等保三级认证，建立完善的安全防护体系。' },
  { year: '2021', title: '产品升级', description: '推出全新v3.0版本，UI全面焕新，性能大幅提升，用户体验迈上新台阶。' },
  { year: '2022', title: '千万用户', description: '累计注册用户突破1000万，成为移动应用领域的标杆产品。' },
  { year: '2023', title: '国际化布局', description: '完成GDPR合规认证，启动海外市场拓展计划，服务全球用户。' },
  { year: '2024', title: '技术突破', description: '底层架构全面重构，引入AI智能推荐引擎，产品性能和质量再创新高。' },
  { year: '2025', title: '生态建设', description: '构建完整的产品生态体系，与众多合作伙伴建立战略联盟，为用户提供更丰富的服务。' },
];

const certifications = [
  { name: 'ISO 27001', description: '信息安全管理体系国际认证', icon: 'ri-shield-check-line' },
  { name: '等保三级', description: '国家信息安全等级保护认证', icon: 'ri-building-2-line' },
  { name: 'App安全认证', description: '国家移动应用安全检测认证', icon: 'ri-smartphone-line' },
  { name: 'GDPR合规', description: '欧盟通用数据保护条例合规', icon: 'ri-global-line' },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://huangguan.app' },
          { '@type': 'ListItem', position: 2, name: '关于我们' },
        ],
      },
      {
        '@type': 'Organization',
        name: '皇冠App',
        url: 'https://huangguan.app',
        description: '皇冠App是深受用户信赖的移动应用平台，提供官方正版下载与安全稳定运行体验。',
        foundingDate: '2018',
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'customer service',
          email: 'support@huangguan.app',
          telephone: '400-XXX-XXXX',
          availableLanguage: ['Chinese'],
          hoursAvailable: [
            { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'], opens: '09:00', closes: '18:00' },
          ],
        },
        address: {
          '@type': 'PostalAddress',
          addressCountry: 'CN',
          addressLocality: '北京',
          addressRegion: '北京市',
        },
      },
      {
        '@type': 'FAQPage',
        mainEntity: [
          { '@type': 'Question', name: '皇冠App是否免费？', acceptedAnswer: { '@type': 'Answer', text: '皇冠App基础功能完全免费。' } },
          { '@type': 'Question', name: '如何联系客服？', acceptedAnswer: { '@type': 'Answer', text: '通过应用内反馈、官方邮箱或客服电话联系。' } },
        ],
      },
    ],
  };

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(aboutJsonLd);
    script.id = 'schema-about';
    const existing = document.getElementById('schema-about');
    if (existing) existing.remove();
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById('schema-about');
      if (el) el.remove();
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');
    setFormError('');

    const form = e.currentTarget;
    const honeypotField = form.querySelector<HTMLInputElement>('[name="phone_alt"]');
    if (honeypotField && honeypotField.value.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    try {
      const formData = new FormData(form);
      if (honeypotField && honeypotField.value.trim() === '') {
        formData.delete('phone_alt');
      }

      const response = await fetch('https://readdy.ai/api/form/d9qtokfnahb7382ovfng', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || '提交失败，请稍后重试';
        setFormError(serverMsg.includes('spam') || serverMsg.includes('form data is spam') ? '提交失败，请稍后重试' : serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <PageLayout>
      <SEO
        title="关于我们 - 皇冠App官方正版平台"
        description="皇冠App品牌介绍，了解我们的发展历程、安全认证、核心团队和联系方式。皇冠App始终以用户安全和体验为首要原则。"
        keywords="皇冠App,关于皇冠App,皇冠App品牌,皇冠App联系方式,皇冠App信誉"
        canonicalUrl="https://huangguan.app/about"
      />

      <div className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>
        </div>

        <section className="py-16 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
            <div className="max-w-3xl mx-auto text-center mb-14">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
                关于皇冠App
              </h1>
              <p className="text-base text-foreground-600 leading-relaxed">
                皇冠App成立于2018年，是一家专注于移动应用开发的科技公司。我们以"安全、稳定、便捷"为核心理念，
                致力于为数千万用户提供高质量的移动应用服务。经过多年的持续创新和技术积累，
                皇冠App已成为行业内备受信赖的移动应用平台。
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-16">
              <div className="bg-background-100 border border-background-200/70 rounded-lg p-6 text-center">
                <p className="text-3xl font-heading font-bold text-primary-600 mb-1">8年+</p>
                <p className="text-sm text-foreground-500">行业深耕经验</p>
              </div>
              <div className="bg-background-100 border border-background-200/70 rounded-lg p-6 text-center">
                <p className="text-3xl font-heading font-bold text-accent-600 mb-1">1000万+</p>
                <p className="text-sm text-foreground-500">累计服务用户</p>
              </div>
              <div className="bg-background-100 border border-background-200/70 rounded-lg p-6 text-center">
                <p className="text-3xl font-heading font-bold text-primary-600 mb-1">99.9%</p>
                <p className="text-sm text-foreground-500">安全运行率</p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-12">
              发展历程
            </h2>
            <div className="max-w-3xl mx-auto">
              {milestones.map((item, index) => (
                <div key={index} className="flex gap-6 pb-10 relative">
                  {index < milestones.length - 1 && (
                    <div className="absolute left-[19px] top-12 bottom-0 w-px bg-background-300"></div>
                  )}
                  <div className="relative z-10 w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-full bg-primary-500 text-background-50">
                    <span className="text-xs font-bold">{item.year.slice(2)}</span>
                  </div>
                  <div className="pt-1">
                    <p className="text-xs text-primary-600 font-medium mb-1">{item.year}</p>
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1">{item.title}</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-12">
              权威认证
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-4xl mx-auto">
              {certifications.map((cert) => (
                <div key={cert.name} className="bg-background-100 border border-background-200/70 rounded-lg p-6 text-center">
                  <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 rounded-full bg-accent-100">
                    <i className={`${cert.icon} text-2xl text-accent-600 w-6 h-6 flex items-center justify-center`}></i>
                  </div>
                  <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-2">{cert.name}</h3>
                  <p className="text-xs text-foreground-500">{cert.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-12">
              联系我们
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 max-w-5xl mx-auto">
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 flex items-center justify-center flex-shrink-0 rounded-md bg-primary-100">
                    <i className="ri-mail-line text-xl text-primary-600 w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                    <p className="text-sm text-foreground-500">support@huangguan.app</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 flex items-center justify-center flex-shrink-0 rounded-md bg-primary-100">
                    <i className="ri-phone-line text-xl text-primary-600 w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-1">客服电话</h3>
                    <p className="text-sm text-foreground-500">400-XXX-XXXX（工作日 9:00 - 18:00）</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 flex items-center justify-center flex-shrink-0 rounded-md bg-primary-100">
                    <i className="ri-map-pin-line text-xl text-primary-600 w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-1">公司地址</h3>
                    <p className="text-sm text-foreground-500">北京市朝阳区XXX大厦XX层</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 flex items-center justify-center flex-shrink-0 rounded-md bg-primary-100">
                    <i className="ri-time-line text-xl text-primary-600 w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-1">工作时间</h3>
                    <p className="text-sm text-foreground-500">周一至周五 9:00 - 18:00（法定节假日除外）</p>
                  </div>
                </div>
              </div>

              <div className="bg-background-50 border border-background-200/70 rounded-lg p-6">
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-5">发送消息</h3>
                <form onSubmit={handleSubmit} data-readdy-form="" className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm text-foreground-700 mb-1.5">姓名</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-3.5 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm text-foreground-700 mb-1.5">邮箱</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-3.5 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm text-foreground-700 mb-1.5">留言内容</label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      required
                      maxLength={500}
                      className="w-full px-3.5 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors resize-none"
                      placeholder="请输入您的留言（不超过500字）"
                    ></textarea>
                  </div>

                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="antispam-field"
                  />

                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full py-3 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-200 whitespace-nowrap cursor-pointer"
                  >
                    {formStatus === 'submitting' ? '发送中...' : '发送消息'}
                  </button>

                  {formStatus === 'success' && (
                    <p className="text-sm text-accent-600 text-center">消息已成功发送，我们会尽快回复您！</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-red-500 text-center">{formError}</p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </div>
    </PageLayout>
  );
}